/**
 * Zahlungsmodalitaeten - 1:1-Portierung aus `index.html` (Stand 2026-08-08).
 *
 * Hier steht, wie bezahlt wird: die Ratenvorlagen der Lieferanten, die Wahl zwischen
 * Anzahlung/Restzahlung und Gesamtzahlung, und die Zustaende einer einzelnen Rate.
 *
 * Werte woertlich uebernommen - einschliesslich der englischen Vorlagenbezeichnungen und der
 * uneinheitlichen Prozent-/Tagesangaben als Zeichenketten. Ergaenzt wurden ausschliesslich Typen.
 */

export interface PaymentTemplatePayment {
  label: string;
  /**
   * Prozentsatz als Zeichenkette (im Original durchgehend so, auch `""` bei den Vorlagen ohne
   * feste Aufteilung).
   */
  pct: string;
  /**
   * Faelligkeit in Tagen nach BL, als Zeichenkette. Fehlt bei den Raten, deren Faelligkeit sich
   * erst aus dem Vorgang ergibt - das ist ein Unterschied zu `dueDays: "0"` und deshalb bewusst
   * nicht vereinheitlicht.
   */
  dueDays?: string;
  /** Strukturierte Faelligkeitsreferenz einer neu angelegten Vorlage. */
  anchor?: "order_date" | "etd" | "eta" | "goods_received" | null;
  /** Ganze Tage relativ zum Anker; negative Werte bedeuten davor. */
  offsetDays?: number;
}

export interface PaymentTemplate {
  id: string;
  label: string;
  payments: readonly PaymentTemplatePayment[];
}

/**
 * Ratenvorlagen fuer Lieferantenkonditionen (index.html:4551-4563). Die Reihenfolge ist die
 * Reihenfolge im Auswahlmenue; der erste Eintrag mit leerer `id` ist der "keine Vorlage"-Fall.
 */
export const PAYMENT_TEMPLATES: readonly PaymentTemplate[] = [
  { id: "", label: "Keine automatische Ratenvorlage", payments: [] },
  {
    id: "100_30_after_bl",
    label: "100% 30-days after BL",
    payments: [{ label: "30-days after BL", pct: "100", dueDays: "30" }],
  },
  {
    id: "10_90_tt_after_bl",
    label: "10% deposit and 90% TT after BL",
    payments: [
      { label: "Deposit", pct: "10", dueDays: "0" },
      { label: "TT after BL", pct: "90" },
    ],
  },
  {
    id: "30_70_tt_after_bl",
    label: "30% deposit and 70% TT after BL",
    payments: [
      { label: "Deposit", pct: "30", dueDays: "0" },
      { label: "TT after BL", pct: "70" },
    ],
  },
  {
    id: "30_70_tt_before_shipment",
    label: "30% deposit and 70% TT before shipment",
    payments: [
      { label: "Deposit", pct: "30", dueDays: "0" },
      { label: "TT before shipment", pct: "70" },
    ],
  },
  {
    id: "100_90_after_bl",
    label: "100% 90-days after BL",
    payments: [{ label: "90-days after BL", pct: "100", dueDays: "90" }],
  },
  {
    id: "20_80_tt_after_bl",
    label: "20% deposit and 80% TT after BL",
    payments: [
      { label: "Deposit", pct: "20", dueDays: "0" },
      { label: "TT after BL", pct: "80" },
    ],
  },
  {
    id: "x_tt_balance_against_bl",
    label: "x deposit by TT and balance against BL",
    payments: [
      { label: "Deposit by TT", pct: "", dueDays: "0" },
      { label: "Balance against BL", pct: "" },
    ],
  },
  {
    id: "15_85_tt_after_bl",
    label: "15% deposit and 85% TT after BL",
    payments: [
      { label: "Deposit", pct: "15", dueDays: "0" },
      { label: "TT after BL", pct: "85" },
    ],
  },
  {
    id: "100_45_after_bl",
    label: "100% 45-days after BL",
    payments: [{ label: "45-days after BL", pct: "100", dueDays: "45" }],
  },
  {
    id: "15_85_tt_30_after_bl",
    label: "15% deposit and 85% TT 30 days after BL",
    payments: [
      { label: "Deposit", pct: "15", dueDays: "0" },
      { label: "TT 30 days after BL", pct: "85", dueDays: "30" },
    ],
  },
];

/**
 * Vorlagenkennung -> Vorlage (index.html:4564). Aus `PAYMENT_TEMPLATES` abgeleitet, deshalb keine
 * eigene Wertquelle - aber ein eigener Wert, der genauso gegen das Original geprueft wird wie die
 * uebrigen Konstanten (angemeldet in `test/helpers/configKonstanten.ts` unter
 * `KONSTANTEN_NAMEN_ABGELEITET`).
 *
 * ACHTUNG fuer aufrufenden Code: `supplierPlanSummary` indiziert diese Tabelle mit einem Wert aus
 * den Daten (`PAYMENT_TEMPLATE_MAP[s.paymentTemplate || '']`, index.html:4997). Die Anlage leitet
 * daraus keine Zahlungsraten mehr ab: Task 13 setzt Zahlungsziele ausschliesslich ueber die
 * Freigabe- und Bestellrouten. Das ist ein Zugriff auf ein Objekt mit `Object.prototype` in
 * der Kette - ein Datensatz mit `paymentTemplate: "constructor"` greift durch und liefert `Object`.
 *
 * Der Durchgriff wird in dieser Phase NICHT abgesichert: weder hier per `Object.create(null)` noch
 * in der lesenden Funktion per `Object.hasOwn`. Beides waere eine Verhaltensaenderung, und die
 * Portierungsphase verlangt woertliche Uebernahme. Der Werttyp ist deshalb bewusst
 * `PaymentTemplate | undefined` - die lesende Stelle muss den fehlenden Eintrag ohnehin abfangen
 * (`t && t.id`, index.html:4998), und ein Typ ohne `undefined` waere an dieser Tabelle eine
 * Zusicherung, die die Daten nicht halten.
 *
 * `as const` an der Paarbildung ist reine Typarbeit (ohne es liest TypeScript den Tupeltyp als
 * `(string | PaymentTemplate)[]` und `Object.fromEntries` passt nicht mehr); der Laufzeitausdruck
 * ist der des Originals.
 */
export const PAYMENT_TEMPLATE_MAP: Readonly<Record<string, PaymentTemplate | undefined>> =
  Object.fromEntries(PAYMENT_TEMPLATES.map((t) => [t.id, t] as const));

export interface PaymentMode {
  label: string;
  /** Anzahl der Raten in diesem Modus. */
  count: number;
}

/** Zahlungsmodus einer Bestellung (index.html:4565-4568). */
export const PAYMENT_MODES: Readonly<Record<string, PaymentMode>> = {
  split: { label: "Anzahlung + Restzahlung", count: 2 },
  total: { label: "Nur Gesamtzahlung", count: 1 },
};

export interface PaymentStatus {
  label: string;
  /** CSS-Klasse fuer die Einfaerbung der Statuspille. */
  className: string;
}

/** Zustaende einer einzelnen Rate (index.html:4569-4573). */
export const PAYMENT_STATUSES: Readonly<Record<string, PaymentStatus>> = {
  open: { label: "offen", className: "open" },
  fixed: { label: "fix", className: "fixed" },
  paid: { label: "bezahlt", className: "paid" },
};

/**
 * Stichtag, ab dem `backfillMissingPaymentRequestTask` (`src/lib/payments.ts`, index.html:5215) eine
 * fehlende Zahlungsanforderungs-Aufgabe nachtraegt: `paymentInitiatedAt`-Werte vor diesem Zeitpunkt
 * werden nicht nachtraeglich in eine Aufgabe umgewandelt (index.html:5181). Woertlich uebernommen -
 * anders als `PAYMENT_TEMPLATE_MAP` keine Ableitung aus einer anderen Konstante, sondern ein
 * eigenstaendiges Literal (siehe `test/helpers/configKonstanten.ts`, `KONSTANTEN_NAMEN_NACHTRAEGLICH`).
 */
export const PAYMENT_TASK_MIGRATION_START = "2026-07-31T09:20:00.000Z";
